// ============================================================================
// AUTHORITATIVE EMAIL STYLING — SYNCED ACROSS 6 FILES
// ============================================================================
// Any change to TAG_STYLES or applyInlineEmailStyles MUST be synced to all 6
// files in a single coordinated commit. Drift between these files is the root
// cause of preview/editor/sent-email rendering mismatches.
//
//   1. scalemediationportal/src/components/template/Account/components/email-editor/emailStyles.js  (this file)
//   2. scalemediation-Admin/src/views/admin/EmailTemplates/email-editor/emailStyles.js
//   3. scalemediation-Admin/src/views/admin/CaseManagers/email-editor/emailStyles.js
//   4. scalemediation-Admin/src/views/admin/CaseManagerEmailTemplates/email-editor/emailStyles.js
//   5. scalemediation-be/src/functions/emailBodyStyles.js
//   6. Scale-Loops-Microservice/services/emailBodyStyles.js
//
// Spacing contract:
//   - Enter (new paragraph):         6px gap (visible, tight flow)
//   - Shift+Enter (<br>):            SAME 6px gap — <br> is converted into
//                                    a paragraph break so Enter and Shift+Enter
//                                    render identically in preview + email
//   - Enter + Enter (blank line):    empty <p>&nbsp;</p>, ~32px visible gap
//   - Merge tag above signature:     same 6px gap as any other paragraph
// ============================================================================

export const TAG_STYLES = {
    p: 'margin: 0; font-size: 15px; line-height: 1.5; color: #000000;',
    h1: 'margin: 0; font-size: 28px; font-weight: 700; line-height: 1.5; color: #000000;',
    h2: 'margin: 0; font-size: 22px; font-weight: 700; line-height: 1.5; color: #000000;',
    h3: 'margin: 0; font-size: 18px; font-weight: 600; line-height: 1.5; color: #000000;',
    ul: 'margin: 0; padding-left: 24px; font-size: 15px; line-height: 1.5; color: #000000; list-style: disc;',
    ol: 'margin: 0; padding-left: 24px; font-size: 15px; line-height: 1.5; color: #000000; list-style: decimal;',
    li: 'margin: 0; padding: 0; display: list-item; line-height: 1.5;',
    a: 'color: #115E59; text-decoration: underline;',
    hr: 'border: none; border-top: 1px solid #e5e7eb; margin: 0;',
    blockquote: 'margin: 0; padding-left: 12px; border-left: 3px solid #e5e7eb; color: #6b7280;',
    strong: 'font-weight: 700;',
    em: 'font-style: italic;',
}

/**
 * Apply inline styles to common tags so the HTML renders consistently in
 * email clients and in the preview pane. Preserves any existing style attr.
 */
export function applyInlineEmailStyles(html) {
    if (!html) return ''
    let out = html

    // Fill empty paragraphs with &nbsp; so they hold a visible blank-line
    // height once TAG_STYLES.p is applied (~32px total). Matches the
    // Enter+Enter blank-line pattern in the Loops reference design.
    // Empty-p gets margin:0 so Enter+Enter produces a gap of exactly one
    // line-height. Non-empty paragraphs untouched.
    out = out.replace(/<p([^>]*)>(\s*(<br[^>]*>)*\s*)<\/p>/gi, (_m, attrs) => {
        const a = attrs || ''
        if (/style\s*=/i.test(a)) {
            return `<p${a.replace(/style\s*=\s*"([^"]*)"/i, (_s, existing) => `style="${existing}; margin: 0; line-height: 1;"`)}>&nbsp;</p>`
        }
        return `<p${a} style="margin: 0; line-height: 1;">&nbsp;</p>`
    })

    // Convert <br>-inside-<p> into paragraph breaks so Enter and Shift+Enter
    // produce identical visible spacing (the 6px <p> margin).
    out = out.replace(/(<br[^>]*>)\s+/gi, '$1')
    out = out.replace(/<p([^>]*)>([\s\S]*?)<\/p>/gi, (match, attrs, inner) => {
        if (!/<br[^>]*>/i.test(inner)) return match
        const parts = inner.split(/<br[^>]*>/i)
        return parts.map((p) => {
            const t = p.trim()
            return t ? `<p${attrs}>${t}</p>` : `<p style="margin: 0; line-height: 1;">&nbsp;</p>`
        }).join('')
    })

    // Stash <a> tags inside buttonBlock divs before the TAG_STYLES pass.
    // Prepending TAG_STYLES.a (color/text-decoration) onto the button's own
    // authored style produced duplicate declarations that Gmail treated as
    // a content boundary and collapsed everything after the button behind
    // its "Show trimmed content" chevron. Restore after styling.
    const buttonAnchors = []
    out = out.replace(
        /(<div[^>]*data-type="buttonBlock"[^>]*>\s*)(<a\b[^>]*>[\s\S]*?<\/a>)(\s*<\/div>)/gi,
        (_m, openDiv, anchorHtml, closeDiv) => {
            const token = `__BTN_ANCHOR_${buttonAnchors.length}__`
            buttonAnchors.push(anchorHtml)
            return `${openDiv}${token}${closeDiv}`
        },
    )

    for (const [tag, style] of Object.entries(TAG_STYLES)) {
        // Match opening tag, with or without existing attributes.
        const re = new RegExp(`<${tag}(\\s[^>]*)?>`, 'gi')
        out = out.replace(re, (match, attrs = '') => {
            const a = attrs || ''
            if (/style\s*=/.test(a)) {
                // Merge: prepend our defaults so existing inline overrides win.
                return `<${tag}${a.replace(/style\s*=\s*"([^"]*)"/i, (_m, existing) => `style="${style} ${existing}"`)}>`
            }
            return `<${tag}${a} style="${style}">`
        })
    }

    // Restore button-block anchors untouched.
    out = out.replace(/__BTN_ANCHOR_(\d+)__/g, (_m, i) => buttonAnchors[Number(i)] || '')

    // Collapse button-adjacent empty-p spacers at render time.
    out = out.replace(/(<p[^>]*>(?:&nbsp;|\u00a0)<\/p>\s*){2,}(?=<div[^>]*data-type="buttonBlock")/gi, '<p>&nbsp;</p>')
    out = out.replace(/(<div[^>]*data-type="buttonBlock"[^>]*>[\s\S]*?<\/div>\s*)(<p[^>]*>(?:&nbsp;|\u00a0)<\/p>\s*){2,}/gi, '$1<p>&nbsp;</p>')

    // Strip <p> wrapper inside <li> — Gmail adds default <p> margins
    out = out.replace(/<li([^>]*)>\s*<p[^>]*>/gi, '<li$1>')
    out = out.replace(/<\/p>\s*<\/li>/gi, '</li>')
    // Collapse whitespace only inside list structures
    out = out.replace(/<\/li>\s+<li/gi, '</li><li')
    out = out.replace(/<ul([^>]*)>\s+<li/gi, '<ul$1><li')
    out = out.replace(/<\/li>\s+<\/ul/gi, '</li></ul')
    out = out.replace(/<ol([^>]*)>\s+<li/gi, '<ol$1><li')
    out = out.replace(/<\/li>\s+<\/ol/gi, '</li></ol')

    // Collapse newlines between sibling block tags. Factory seed HTML is
    // pretty-printed with \n between block elements; Gmail can render those
    // text nodes as visible gaps. Matches BE behavior so preview == sent email.
    out = out.replace(/(<\/(?:p|ul|ol|li|div|h1|h2|h3|blockquote)>)\n+(<(?:p|ul|ol|li|div|h1|h2|h3|blockquote)\b)/gi, '$1$2')

    return out
}

// ============================================================================
// wrapEmailBody — also used at server render time. Exported so the preview
// pane can wrap content identically to what the sent email receives.
// ============================================================================
export function wrapEmailBody(html) {
    return (
        '<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width: 100%;">' +
            '<tr>' +
                '<td style="padding: 24px 0;">' +
                    '<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width: 100%; background-color: #FFFFFF; border-radius: 4px;">' +
                        '<tr>' +
                            '<td style="padding: 20px 24px; font-family: \'Helvetica Neue\', Helvetica, Arial, sans-serif; color: #000000; font-size: 15px; line-height: 1.5;">' +
                                html +
                            '</td>' +
                        '</tr>' +
                    '</table>' +
                '</td>' +
            '</tr>' +
        '</table>'
    )
}

// Scoped CSS string injected into editor + preview wrappers so the
// live editor and preview look identical to the sent email.
export const EMAIL_BODY_CSS = `
.email-body-content { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #000000; font-size: 15px; line-height: 1.5; }
.email-body-content p { ${TAG_STYLES.p} }
/* Empty-paragraph handling — TWO mechanisms, different surfaces (not duplicates):
   1. These CSS pseudo-rules apply ONLY to the tip-tap editor canvas, where
      a real empty <p></p> DOM node exists. The &nbsp; is injected visually so
      the user sees a blank line while typing.
   2. applyInlineEmailStyles() separately injects &nbsp; as real text content
      into empty <p></p> for the rendered HTML (preview + sent email) — email
      clients need literal content, not CSS ::before.
   Both must stay aligned if one is changed; they solve the same problem for
   two different rendering contexts. */
.email-body-content p:empty::before { content: "\\00a0"; }
.email-body-content p.is-empty::before { content: "\\00a0"; }
.email-body-content h1 { ${TAG_STYLES.h1} }
.email-body-content h2 { ${TAG_STYLES.h2} }
.email-body-content h3 { ${TAG_STYLES.h3} }
.email-body-content ul { ${TAG_STYLES.ul} list-style: disc !important; }
.email-body-content ol { ${TAG_STYLES.ol} list-style: decimal !important; }
.email-body-content li { ${TAG_STYLES.li} display: list-item !important; }
.email-body-content li p { margin: 0; }
.email-body-content a { ${TAG_STYLES.a} }
.email-body-content hr { ${TAG_STYLES.hr} }
.email-body-content blockquote { ${TAG_STYLES.blockquote} }
.email-body-content strong { ${TAG_STYLES.strong} }
.email-body-content em { ${TAG_STYLES.em} }
`
